import { tabKey } from './tabKey';
import { HOME_PATHNAME, MAX_TABS, type LopTab, type LopTabLocation, type TabsState } from './types';

/** Metadata TabRouterSync/openOrFocus need to stamp on a freshly-created or
 *  freshly-mutated tab. Resolved from `Pages` (displayName/iconName) by the
 *  caller — the reducer itself knows nothing about the Pages table. */
export interface TabMeta {
  title: string;
  iconName: string | null;
}

export type TabsAction =
  // ---- Router-driven (TabRouterSync only) ----
  // PUSH: key exists -> refresh its location + focus; else -> new tab after
  // the active one (browser-like), respecting the MAX_TABS cap. Also used
  // for POP (focus-or-open a possibly-closed tab's key) — plan §3.2 treats
  // both the same way once we're past the PUSH-only "insert after active"
  // nuance, which is harmless to reuse for POP recovery.
  | { type: 'NAV_OPEN_OR_FOCUS'; location: LopTabLocation; meta: TabMeta }
  // REPLACE: key exists elsewhere -> close the active tab, focus the
  // existing one. Otherwise -> mutate the active tab's own location/key in
  // place (e.g. /fund-form -> /fund-form?id=9 right after a save).
  | { type: 'NAV_REPLACE'; location: LopTabLocation; meta: TabMeta }
  // Tab-bar activation (`navigate(tab.location, {replace:true, state:{lopTabActivate:true}})`)
  // — pure focus, never touches the tabs array.
  | { type: 'ACTIVATE'; key: string }
  // ---- User-driven (LopTabBar / context menu) ----
  | { type: 'CLOSE'; key: string }
  | { type: 'CLOSE_OTHERS'; key: string }
  | { type: 'CLOSE_ALL' }
  // A router-driven navigation was refused outright because it would need a
  // brand-new tab slot at MAX_TABS (D5: block). TabRouterSync makes the
  // block/revert decision itself (it also has to un-navigate the router) and
  // dispatches this with the location it was trying to reach so
  // LopTabsCapModal can show it and, on אישור, perform it as a normal push
  // once a slot frees up — this action never touches tabs/activeKey/mru.
  | { type: 'CAP_BLOCKED'; location: LopTabLocation }
  // LopTabsCapModal only — ביטול/Escape/backdrop click, or a successful
  // אישור (which clears it itself before navigating). Never touches
  // tabs/activeKey/mru.
  | { type: 'CAP_DISMISS' }
  // ---- Screens opt in via useTabDirty/useTabTitle (plan §3.6) ----
  | { type: 'SET_DIRTY'; key: string; dirty: boolean }
  | { type: 'SET_TITLE'; key: string; title: string };

function withMru(mru: string[], key: string): string[] {
  return [key, ...mru.filter((k) => k !== key)];
}

function makeTab(key: string, location: LopTabLocation, meta: TabMeta, pinned: boolean): LopTab {
  return {
    key,
    location,
    title: meta.title,
    iconName: meta.iconName,
    pinned,
    dirty: false,
    openedAt: Date.now(),
  };
}

export function makeHomeTab(): LopTab {
  return makeTab(
    HOME_PATHNAME,
    { pathname: HOME_PATHNAME, search: '', hash: '' },
    { title: HOME_PATHNAME, iconName: 'Home' },
    true,
  );
}

export function initialTabsState(): TabsState {
  const home = makeHomeTab();
  return { tabs: [home], activeKey: home.key, mru: [home.key], capBlocked: null };
}

/** Picks the tab to focus after `key` is removed: the next entry in MRU
 *  order, falling back to the home tab (always present). */
function pickFallbackActive(state: TabsState, removedKey: string): string {
  const nextMru = state.mru.filter((k) => k !== removedKey);
  return nextMru[0] ?? HOME_PATHNAME;
}

/**
 * Decision D5 predicate: would handling `location` (arriving via
 * `navigationType`) need a genuinely NEW tab slot? Used by TabRouterSync to
 * decide whether the MAX_TABS cap applies — mirrors the reducer's own
 * branches exactly:
 *  - An already-open key never needs a new slot (it's a focus, not an open).
 *  - PUSH/POP (NAV_OPEN_OR_FOCUS) always needs one for an unknown key.
 *  - REPLACE only needs one when it would fall through to open-or-focus
 *    behavior (the active tab is pinned or already gone) — REPLACE's other
 *    two branches (collapse into an existing tab, mutate the active tab in
 *    place) never add a tab, so the cap never applies to them.
 */
export function tabNeedsNewSlot(
  tabs: LopTab[],
  activeKey: string,
  location: LopTabLocation,
  navigationType: string,
): boolean {
  const key = tabKey(location);
  if (tabs.some((t) => t.key === key)) return false;
  if (navigationType !== 'REPLACE') return true;
  const active = tabs.find((t) => t.key === activeKey);
  return !active || active.pinned;
}

export function tabsReducer(state: TabsState, action: TabsAction): TabsState {
  switch (action.type) {
    case 'NAV_OPEN_OR_FOCUS': {
      const key = tabKey(action.location);
      const idx = state.tabs.findIndex((t) => t.key === key);
      if (idx !== -1) {
        const tabs = state.tabs.slice();
        const existing = tabs[idx];
        // Pinned home never takes a router-derived title; a page that called
        // useTabTitle (titleOverride) keeps its own title/icon across a mere
        // re-focus too — only the frozen `location` (e.g. a refreshed
        // `?search=`) actually needs updating in that case.
        tabs[idx] =
          existing.pinned || existing.titleOverride
            ? { ...existing, location: action.location }
            : { ...existing, location: action.location, title: action.meta.title, iconName: action.meta.iconName };
        return { ...state, tabs, activeKey: key, mru: withMru(state.mru, key) };
      }
      // New tab needed. Decision D5 (block, not overwrite): at the cap we
      // refuse outright — no tab is added, changed, or closed, ever. This is
      // also enforced up-front in TabRouterSync (which additionally has to
      // revert the router's own location back to the active tab's URL) — this
      // branch is a defensive backstop for any future direct dispatch that
      // doesn't go through TabRouterSync.
      if (state.tabs.length >= MAX_TABS) {
        return { ...state, capBlocked: action.location };
      }
      const activeIdx = state.tabs.findIndex((t) => t.key === state.activeKey);
      const newTab = makeTab(key, action.location, action.meta, false);
      const tabs = state.tabs.slice();
      tabs.splice(activeIdx === -1 ? tabs.length : activeIdx + 1, 0, newTab);
      return { ...state, tabs, activeKey: key, mru: withMru(state.mru, key) };
    }

    case 'NAV_REPLACE': {
      const activeIdx = state.tabs.findIndex((t) => t.key === state.activeKey);
      // The pinned home tab's identity is never up for grabs — not for the
      // "collapse" branch (would close it) nor "mutate in place" (would
      // silently turn it into whatever page replaced it, e.g. the very first
      // post-login `navigate(from, {replace:true})` firing while home is
      // still the sole/active tab). Fall back to open-or-focus, which simply
      // opens/focuses a SEPARATE tab and leaves home untouched.
      if (activeIdx === -1 || state.tabs[activeIdx].pinned) {
        return tabsReducer(state, { type: 'NAV_OPEN_OR_FOCUS', location: action.location, meta: action.meta });
      }
      const key = tabKey(action.location);
      const existingIdx = state.tabs.findIndex((t) => t.key === key);
      if (existingIdx !== -1 && existingIdx !== activeIdx) {
        // Collapse: close the active tab, focus the existing one (e.g. a
        // record-form REPLACE-redirect lands on an id already open elsewhere).
        const activeTab = state.tabs[activeIdx];
        const tabs = state.tabs.filter((_, i) => i !== activeIdx);
        const focusIdx = tabs.findIndex((t) => t.key === key);
        if (focusIdx !== -1) tabs[focusIdx] = { ...tabs[focusIdx], location: action.location };
        const mru = withMru(
          state.mru.filter((k) => k !== (activeTab?.key ?? '')),
          key,
        );
        return { ...state, tabs, activeKey: key, mru };
      }
      // Mutate the active (non-pinned) tab in place. Its IDENTITY changes
      // here (e.g. /fund-form -> /fund-form?id=9 right after a save), so any
      // prior useTabTitle override is stale — reset it; the page's own
      // effect re-fires immediately after adopting the saved record and
      // reapplies a fresh title/titleOverride for the new identity.
      const tabs = state.tabs.slice();
      tabs[activeIdx] = {
        ...tabs[activeIdx],
        key,
        location: action.location,
        title: action.meta.title,
        iconName: action.meta.iconName,
        titleOverride: false,
      };
      const mru = withMru(
        state.mru.filter((k) => k !== state.activeKey),
        key,
      );
      return { ...state, tabs, activeKey: key, mru };
    }

    case 'ACTIVATE': {
      if (!state.tabs.some((t) => t.key === action.key)) return state;
      if (state.activeKey === action.key) return state;
      return { ...state, activeKey: action.key, mru: withMru(state.mru, action.key) };
    }

    case 'CLOSE': {
      const tab = state.tabs.find((t) => t.key === action.key);
      if (!tab || tab.pinned) return state; // home can't be closed
      const tabs = state.tabs.filter((t) => t.key !== action.key);
      const mru = state.mru.filter((k) => k !== action.key);
      const activeKey = state.activeKey === action.key ? pickFallbackActive(state, action.key) : state.activeKey;
      return { ...state, tabs, activeKey, mru: activeKey === state.activeKey ? mru : withMru(mru, activeKey) };
    }

    case 'CLOSE_OTHERS': {
      const keep = new Set([action.key, HOME_PATHNAME]);
      const tabs = state.tabs.filter((t) => keep.has(t.key) || t.pinned);
      const survivingKeys = new Set(tabs.map((t) => t.key));
      const mru = state.mru.filter((k) => survivingKeys.has(k));
      const activeKey = survivingKeys.has(state.activeKey) ? state.activeKey : action.key;
      return { ...state, tabs, activeKey, mru: withMru(mru, activeKey) };
    }

    case 'CLOSE_ALL': {
      const tabs = state.tabs.filter((t) => t.pinned);
      const home = tabs.find((t) => t.pinned) ?? makeHomeTab();
      return { ...state, tabs: tabs.length ? tabs : [home], activeKey: home.key, mru: [home.key] };
    }

    case 'SET_DIRTY': {
      const idx = state.tabs.findIndex((t) => t.key === action.key);
      if (idx === -1 || state.tabs[idx].dirty === action.dirty) return state;
      const tabs = state.tabs.slice();
      tabs[idx] = { ...tabs[idx], dirty: action.dirty };
      return { ...state, tabs };
    }

    case 'SET_TITLE': {
      const idx = state.tabs.findIndex((t) => t.key === action.key);
      if (idx === -1) return state;
      if (state.tabs[idx].title === action.title && state.tabs[idx].titleOverride) return state;
      const tabs = state.tabs.slice();
      tabs[idx] = { ...tabs[idx], title: action.title, titleOverride: true };
      return { ...state, tabs };
    }

    case 'CAP_BLOCKED':
      return { ...state, capBlocked: action.location };

    case 'CAP_DISMISS':
      return { ...state, capBlocked: null };

    default:
      return state;
  }
}
